import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '@/utils/logger';

/** Props for the fallback render function — receives error details for contextual UI */
interface ErrorFallbackProps {
  error: Error;
  componentStack?: string | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Fallback UI when an error is caught.
   * Accepts a ReactNode for simple cases, or a render function
   * that receives { error, componentStack } for contextual fallbacks.
   */
  fallback?: ReactNode | ((props: ErrorFallbackProps) => ReactNode);
  /** Optional label for logging context (e.g. "OrdersTab", "BookDetails") */
  context?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const ctx = this.props.context ?? 'unknown';

    // Structured logging via project logger (safe in production)
    logger.error(`[ErrorBoundary:${ctx}] Caught render error`, error);

    if (info.componentStack) {
      logger.debug(`[ErrorBoundary:${ctx}] Component stack`, info.componentStack);
    }

    // Preserve component stack for contextual fallback rendering
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      const { error, componentStack } = this.state;

      // Render-function fallback (receives error context)
      if (typeof fallback === 'function' && error) {
        return fallback({ error, componentStack }) as ReactNode;
      }

      // Static ReactNode fallback
      if (fallback && typeof fallback !== 'function') {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-secondary/30 flex items-center justify-center px-4">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              Something went wrong
            </h1>
            <p className="text-muted-foreground mb-4">
              An unexpected error occurred while loading this section. Please try refreshing the page or return to the homepage.
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
              >
                Refresh Page
              </button>
              <a
                href="/"
                className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                Go to Homepage
              </a>
              <button
                onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = '/')}
                className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                Go Back
              </button>
            </div>
            {error && (
              <pre className="mt-4 text-xs text-muted-foreground whitespace-pre-wrap break-words text-left bg-muted p-3 rounded">
                {error.message}
                {import.meta.env.DEV && componentStack && `\n\nComponent Stack:${componentStack}`}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
export type { ErrorFallbackProps, ErrorBoundaryProps };
